import { Task } from '@/utils/types/task'
import { computed, ref } from 'vue'
import { useRoute, useStore } from '@/plugins'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { openDialog } from '@/dialog'
import { useCan } from '@/composables/useCan'
import {
  taskControlUserCreate,
  taskControlUserDelete,
  taskFinalComplete,
  taskObserverUserCreate,
  taskObserverUserDelete,
  taskRevision,
  taskUpdateDeadline,
  taskUpdateExecutor,
} from '@/requests/task'

export function useSidebar(task: Task) {
  const store = useStore()
  const route = useRoute()
  const { can } = useCan()
  const absenceId = route?.params?.absenceId
  const userId = computed(() => store.state.user.userData?.id)
  const isExecutor = computed(() =>
    [task.executorId, task.executor?.id].includes(userId.value)
  )
  const previousDeadline = ref<any>(null)

  const taskType = computed(() => {
    return {
      execution: task.typeId === 1,
      familiarization: task.typeId === 2,
      errand: task.typeId === 3,
    }
  })

  const getStatusCard = computed(() => [
    {
      showIf: task.status?.id === 1,
      title: `${task.status?.name} з ${task.created_at}`,
      class: 'bg-yellow-lighten-1',
    },
    {
      showIf: task.status?.id === 2,
      title: `Завдання ${task.status?.name.toLowerCase()}`,
      class: 'bg-blue',
    },
    {
      showIf: task.status?.id === 3,
      title: `Завдання ${task.status?.name.toLowerCase()} ${
        task.completedDate
      }`,
      class: 'bg-green',
    },
    {
      showIf: task.status?.id === 5,
      title: task.status?.name,
      class: 'bg-yellow-lighten-1',
    },
  ])

  const visibleStatusCards = computed(() =>
    getStatusCard.value.filter(status => status.showIf)
  )

  const createObserverUser = (id: any) => {
    const req = {
      taskId: task.id,
      userId: id,
    }
    return taskObserverUserCreate(req).then(res => {
      task.observers.push(res)
    })
  }

  const observersDialog = () => {
    can('tasks.update', () => {
      openDialog({
        name: 'SelectDepartmentPerson',
        dialogData: {
          userIds: task.observers.map(o => o.userId),
          canBeEmpty: true,
        },
        params: {
          cb: ({ usersToCreate, usersToDelete }) => {
            handleAsync('updateObservers', async () => {
              usersToDelete.forEach((userId: any) => {
                const user = task.observers.find(u => u.userId === userId)
                if (user) {
                  deleteObserverUser(user.id)
                }
              })
              usersToCreate.forEach(createObserverUser)
            })
          },
        },
      })
    })
  }

  const deleteObserverUser = (userId: number) => {
    can('tasks.delete', () => {
      return taskObserverUserDelete(userId, absenceId).then(() => {
        task.observers = task.observers.filter(item => item.id !== userId)
      })
    })
  }

  const createControlUser = (id: any) => {
    can('tasks.update', () => {
      const req = {
        taskId: task.id,
        userId: id,
      }
      return taskControlUserCreate(req).then(res => {
        task.controlUsers.push(res)
      })
    })
  }

  const deleteControlUser = (userId: number) => {
    can('tasks.update', () => {
      return taskControlUserDelete(userId, absenceId).then(() => {
        task.controlUsers = task.controlUsers.filter(item => item.id !== userId)
      })
    })
  }

  const controlUsersDialog = () => {
    can('tasks.update', () => {
      openDialog({
        name: 'SelectDepartmentPerson',
        dialogData: {
          userIds: task.controlUsers.map(o => o.userId),
          canBeEmpty: true,
        },
        params: {
          cb: ({ usersToCreate, usersToDelete }) => {
            handleAsync('updateControlUsers', async () => {
              usersToDelete.forEach((userId: any) => {
                const user = task.controlUsers.find(u => u.userId === userId)
                if (user) {
                  deleteControlUser(user.id)
                }
              })
              usersToCreate.forEach(createControlUser)
            })
          },
        },
      })
    })
  }

  const updateExecutor = (userId: number) => {
    can('tasks.update', () => {
      if (!userId) return
      return handleAsync('updateExecutor', () => {
        return taskUpdateExecutor(task.id, userId).then(res => {
          task.executor = res.executor
          task.executorId = res.executorId
        })
      })
    })
  }

  const executorDialog = () => {
    can('tasks.update', () => {
      openDialog({
        name: 'SelectDepartmentPerson',
        dialogData: {
          userIds: [task.executorId],
          isSingle: true,
        },
        params: {
          cb: ({ userIds }) => {
            updateExecutor(userIds[0])
          },
        },
      })
    })
  }

  const updateDeadline = async (): Promise<void> => {
    can('tasks.update', () => {
      if (task.deadline === previousDeadline.value) {
        return
      }

      previousDeadline.value = task.deadline

      return handleAsync('updateDeadline', async () => {
        if (task.deadline && task.deadline.length === 17) {
          await taskUpdateDeadline(task.id, task.deadline, absenceId)
        }
      })
    })
  }

  const finalComplete = async () => {
    can('tasks.complete', () => {
      return handleAsync('finalCompleteHandler', async () => {
        await taskFinalComplete(task.id, absenceId).then(res => {
          Object.assign(task, res)
          setSnackbar({ text: 'Завдання перевірено і завершено' })
        })
      })
    })
  }

  const revisionTask = async (): Promise<void> => {
    can('task.revision', () => {
      return handleAsync('revisionTaskHandler', async () => {
        await taskRevision(task.id, absenceId).then(res => {
          Object.assign(task, res)
          setSnackbar({
            text: 'Завдання відправлено на доопрацювання виконавцю',
          })
        })
      })
    })
  }

  return {
    updateExecutor,
    createControlUser,
    deleteObserverUser,
    createObserverUser,
    deleteControlUser,
    isExecutor,
    getStatusCard,
    visibleStatusCards,
    taskType,
    finalComplete,
    revisionTask,
    updateDeadline,
    observersDialog,
    controlUsersDialog,
    executorDialog,
  }
}
